/**
 * MarketProvider - SINGLE SOURCE OF TRUTH for all Market Data
 * 
 * ARCHITECTURE:
 * - WebSocket initialized ONCE when App mounts, NEVER closed on tab switch
 * - Global store maintains data for ALL symbols simultaneously
 * - Components simply read from context, no local WebSocket state
 * - Connection status is GLOBAL and STABLE
 * 
 * This fixes:
 * 1. "Disconnected -> Closed -> Live" flicker on tab switch
 * 2. Zero data on symbol switch
 * 3. Data loss during component unmount/remount
 */

import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
// SESSION 109: Force logout handled via window.dispatchEvent('force_logout')

const API = process.env.REACT_APP_BACKEND_URL || '';

// ============================================================
// GLOBAL STORE STRUCTURE
// ============================================================
const createDefaultSymbolData = () => ({
  // Prices
  futuresPrice: 0,
  spotPrice: 0,
  ltp: null,
  
  // Session Stats
  sessionStats: {
    cumulative_delta: 0,
    session_volume: 0,
    vwap: 0,
    tick_count: 0,
    alert_count: 0,
    lee_ready_cvd: 0,  // SESSION 121: Lee-Ready CVD for comparison with GoCharting
    aggressor_cvd: 0,  // SESSION 127: Aggressor CVD for comparison
  },
  
  // SESSION 86i: CVD Anchor System
  // The initial anchor is the cumulative_delta received on first connection (from API/INIT)
  // This represents the day's CVD BEFORE the bot started
  // live_session_cvd is what accumulates from the HFT engine (starts from 0)
  // True Absolute CVD = cvd_anchor + live_session_cvd
  cvd_anchor: null,           // Initial anchor (set once on connection, never overwritten)
  live_session_cvd: 0,        // Live session CVD from hft_breakdown (since bot start)
  live_cvd: 0,                // Calculated: anchor + session (displayed in UI)
  live_delta_slope: 0,        // Live delta slope (preserved on lightweight ticks)
  
  // GEX Data
  gexLevels: null,
  
  // God Score & Strategy
  godScore: null,
  strategyPulse: null,
  tridentStatus: null,
  hftSignal: null,
  
  // Market Profile
  marketProfile: null,
  dpocLineData: [],
  
  // Charts
  candles: [],
  latestCandle: null,
  deltaData: [],
  cumulativeDeltaData: [],
  vwapData: [],
  hftCandles: [],
  pocLineData: [],
  
  // Tape & Alerts
  tape: [],
  alerts: [],
  tradingStats: null,
  tradeEvents: [],
  
  // Metadata
  lastUpdated: 0,
});

// ============================================================
// CONTEXT
// ============================================================
const MarketContext = createContext(null);

export const MarketProvider = ({ children }) => {
  // === GLOBAL STATE ===
  const [store, setStore] = useState({
    NIFTY: createDefaultSymbolData(),
    BANKNIFTY: createDefaultSymbolData(),
  });
  
  // === ACTIVE SYMBOL (UI display only) ===
  const [activeSymbol, setActiveSymbol] = useState('NIFTY');
  
  // === AUDIO ALERTS STATE (Session 39 - Smart Audio) ===
  // Default OFF for safety - users must explicitly enable
  const [audioEnabled, setAudioEnabled] = useState(() => {
    // Check localStorage for user preference on initial mount
    const saved = localStorage.getItem('user_audio_preference');
    return saved === 'true';
  });
  
  // Toggle audio and persist to localStorage
  const toggleAudio = useCallback(() => {
    setAudioEnabled(prev => {
      const newState = !prev;
      localStorage.setItem('user_audio_preference', String(newState));
      
      // Play test sound when ENABLING so user knows speakers work
      if (newState) {
        try {
          // Short beep using Web Audio API
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.value = 800; // Hz
          oscillator.type = 'sine';
          gainNode.gain.value = 0.3;
          
          oscillator.start();
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
          oscillator.stop(audioContext.currentTime + 0.2);
          
          console.log('🔔 Audio alerts ENABLED - test beep played');
        } catch (e) {
          console.warn('Could not play test sound:', e);
        }
      } else {
        console.log('🔕 Audio alerts DISABLED');
      }
      
      return newState;
    });
  }, []);
  
  // === CONNECTION STATE (NEVER resets on symbol switch) ===
  const [connectionStatus, setConnectionStatus] = useState({
    status: 'connecting',  // 'connecting', 'live', 'disconnected', 'market_closed'
    dataSource: 'none',
    isConnected: false,
    isMarketOpen: false,
    wasConnected: false,  // Track if we've ever been connected (prevents banner flash on reconnect)
  });
  
  // === REFS ===
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const isConnectedRef = useRef(false);
  const isMarketOpenRef = useRef(false);  // STRICT FREEZE: Track market status for synchronous checks
  const activeSymbolRef = useRef(activeSymbol);  // SESSION 86j: Ref for activeSymbol to avoid dependency chains
  
  // === MESSAGE BATCHING ("BUTTER FIX") ===
  // Prevents UI freeze by batching high-frequency WebSocket updates
  // Instead of rendering 50+ times/second, we render max 10 times/second (100ms batches)
  const messageBufferRef = useRef([]);
  const isThrottledRef = useRef(false);
  const throttleTimerRef = useRef(null);
  const BATCH_INTERVAL_MS = 100; // 100ms = 10 FPS max render rate
  
  // Keep ref in sync with state
  useEffect(() => {
    isMarketOpenRef.current = connectionStatus.isMarketOpen;
  }, [connectionStatus.isMarketOpen]);
  
  // SESSION 86j: Keep activeSymbolRef in sync with activeSymbol state
  useEffect(() => {
    activeSymbolRef.current = activeSymbol;
  }, [activeSymbol]);
  
  // ============================================================================
  // SESSION 58/59: INSTITUTIONAL-GRADE STATE PERSISTENCE + SANITY FILTERS
  // ============================================================================
  // "Last Known Good" (LKG) validation with atomic state persistence.
  // The Rule: If incoming value is 0/null/undefined but previous was valid,
  // REJECT the update and hold LKG value for 3-second cooldown.
  // 
  // SESSION 59: Added deviation detection for values that aren't exactly 0
  // but are suspiciously small (e.g., GEX dropping from 683Cr to 0.4Cr)
  // ============================================================================
  
  const LKG_COOLDOWN_MS = 3000; // 3-second cooldown for zero-rejection
  // SESSION 120: CVD_MAX_JUMP_PERCENT removed - backend is now SSOT for CVD
  // The previous client-side jump filter was causing anchoring issues
  const GEX_DEVIATION_THRESHOLD = 0.10; // Reject if GEX drops below 10% of previous

  // === HELPER: Check if value is "invalid" (zero, null, undefined, dash, NaN) ===
  const isInvalidValue = (val) => {
    if (val === null || val === undefined) return true;
    if (val === 0) return true;
    if (val === '-' || val === '--' || val === '—') return true;
    if (typeof val === 'string' && (val.trim() === '' || val.trim() === '-')) return true;
    if (typeof val === 'number' && isNaN(val)) return true;
    return false;
  };
  
  // === SESSION 59: Check if value has suspicious deviation (>90% drop) ===
  const hasSuspiciousDeviation = (newVal, oldVal, threshold = GEX_DEVIATION_THRESHOLD) => {
    // Only check numeric values
    if (typeof newVal !== 'number' || typeof oldVal !== 'number') return false;
    if (oldVal === 0 || oldVal === null || oldVal === undefined) return false;
    
    const oldAbs = Math.abs(oldVal);
    const newAbs = Math.abs(newVal);
    
    // If old value was significant (>100) and new is tiny (<10% of old), suspicious
    if (oldAbs > 100 && newAbs < (oldAbs * threshold)) {
      console.warn(`[LKG Deviation] 🛡️ Suspicious drop: ${oldVal} → ${newVal} (rejected)`);
      return true;
    }
    return false;
  };
  
  // === HELPER: Deep merge with LKG protection for nested objects ===
  // If one field in object is missing, don't wipe out the whole object
  // SESSION 59: Also checks for suspicious deviation (>90% drop)
  const deepMergeWithLKG = (current, incoming, cooldownActive) => {
    if (!incoming || typeof incoming !== 'object') {
      // If incoming is invalid and cooldown active, keep current
      if (isInvalidValue(incoming) && cooldownActive && current !== undefined) {
        return current;
      }
      // SESSION 59: Check for suspicious deviation even if not exactly zero
      if (typeof incoming === 'number' && typeof current === 'number' && cooldownActive) {
        if (hasSuspiciousDeviation(incoming, current)) {
          return current;
        }
      }
      return incoming;
    }
    
    if (!current || typeof current !== 'object') {
      return incoming;
    }
    
    const merged = { ...current };
    
    for (const key in incoming) {
      const incomingVal = incoming[key];
      const currentVal = current[key];
      
      // ZERO-REJECTION FILTER for critical numeric fields
      if (isInvalidValue(incomingVal) && !isInvalidValue(currentVal) && cooldownActive) {
        // REJECT: Keep current value, incoming is invalid
        console.log(`[LKG] Holding ${key}: rejected ${incomingVal} → keeping ${currentVal}`);
        continue; // Don't update this field
      }
      
      // SESSION 59: DEVIATION FILTER - catch values that aren't 0 but are suspiciously small
      if (typeof incomingVal === 'number' && typeof currentVal === 'number' && cooldownActive) {
        if (hasSuspiciousDeviation(incomingVal, currentVal)) {
          console.log(`[LKG] Holding ${key}: deviation filter rejected ${incomingVal} → keeping ${currentVal}`);
          continue; // Don't update this field
        }
      }
      
      // Recursively merge nested objects
      if (typeof incomingVal === 'object' && incomingVal !== null && !Array.isArray(incomingVal)) {
        merged[key] = deepMergeWithLKG(currentVal, incomingVal, cooldownActive);
      } else {
        merged[key] = incomingVal;
      }
    }
    
    return merged;
  };

  // === UPDATE SYMBOL DATA (INSTITUTIONAL-GRADE PERSISTENCE) ===
  const updateSymbolData = useCallback((symbol, updates) => {
    // Validate symbol is one of our supported symbols
    if (!symbol || !['NIFTY', 'BANKNIFTY'].includes(symbol)) return;
    
    setStore(prev => {
      const currentData = prev[symbol] || createDefaultSymbolData();
      const currentAge = Date.now() - (currentData.lastUpdated || 0);
      const cooldownActive = currentAge < LKG_COOLDOWN_MS;
      
      // Start with shallow copy
      const retainedUpdates = { ...updates };
      
      // ================================================================
      // ATOMIC STATE PERSISTENCE: Zero-Rejection Filter
      // ================================================================
      
      // === 1. GOD SCORE PROTECTION ===
      if (retainedUpdates.godScore !== undefined) {
        const newScore = retainedUpdates.godScore;
        const currentScore = currentData.godScore;
        
        // Check if incoming godScore is completely null/undefined
        if (newScore === null || newScore === undefined) {
          if (cooldownActive && currentScore) {
            retainedUpdates.godScore = currentScore;
            console.log('[LKG] God Score: Holding previous (incoming null)');
          }
        } else if (typeof newScore === 'object') {
          // Deep merge with LKG protection for nested fields
          retainedUpdates.godScore = deepMergeWithLKG(currentScore, newScore, cooldownActive);
          
          // CRITICAL: Protect total_score from dropping to 0
          if (isInvalidValue(retainedUpdates.godScore?.total_score) && 
              !isInvalidValue(currentScore?.total_score) && cooldownActive) {
            retainedUpdates.godScore.total_score = currentScore.total_score;
            console.log(`[LKG] God Score total: Holding ${currentScore.total_score}`);
          }
          
          // SESSION 59: Protect God Score from suspicious deviation (>50 point drop)
          if (typeof retainedUpdates.godScore?.total_score === 'number' && 
              typeof currentScore?.total_score === 'number' && cooldownActive) {
            const scoreDrop = currentScore.total_score - retainedUpdates.godScore.total_score;
            if (scoreDrop > 50 && retainedUpdates.godScore.total_score < 10) {
              retainedUpdates.godScore.total_score = currentScore.total_score;
              console.log(`[LKG] God Score: Rejected ${scoreDrop.toFixed(0)} point drop → Holding ${currentScore.total_score}`);
            }
          }
          
          // Protect component scores (hft_score, gamma_score, profile_score)
          ['hft_score', 'gamma_score', 'profile_score'].forEach(field => {
            if (isInvalidValue(retainedUpdates.godScore?.[field]) && 
                !isInvalidValue(currentScore?.[field]) && cooldownActive) {
              retainedUpdates.godScore[field] = currentScore[field];
            }
          });
          
          // SESSION 59: Protect VIX value from becoming "-" or undefined
          if (retainedUpdates.godScore?.vix_value !== undefined) {
            const newVix = retainedUpdates.godScore.vix_value;
            const currentVix = currentScore?.vix_value;
            
            // VIX protection: Don't allow dash, NaN, or sudden 0
            if (isInvalidValue(newVix) && !isInvalidValue(currentVix) && cooldownActive) {
              retainedUpdates.godScore.vix_value = currentVix;
              console.log(`[LKG] VIX: Rejected ${newVix} → Holding ${currentVix}`);
            }
          }
          
          // === SESSION 120: CVD FLOWS DIRECTLY FROM BACKEND SSOT ===
          // The backend now uses get_display_cvd() as the Single Source of Truth.
          // We TRUST the backend completely - no client-side filtering.
          // The previous "CVD JUMP FILTER" was causing anchoring issues by
          // rejecting valid backend updates when values changed significantly.
          // If CVD looks wrong, the bug is in the backend, not here.
          // 
          // REMOVED: CVD_MAX_JUMP_PERCENT filter that was blocking valid updates
          // REMOVED: isHydrationAnomaly detection that was rejecting legitimate CVD changes
        }
      }
      
      // === 2. GEX LEVELS PROTECTION (Deep field protection) ===
      // SESSION 66 FIX: Skip cooldown protection for GEX when status is LIVE
      // The cooldown was blocking ALL GEX updates because data flows continuously
      if (retainedUpdates.gexLevels !== undefined) {
        const newGex = retainedUpdates.gexLevels;
        const currentGex = currentData.gexLevels;
        
        // SESSION 66: If new GEX has status LIVE, trust it completely (bypass cooldown)
        const isLiveGex = newGex?.status === 'LIVE' || newGex?.greeks_source === 'LIVE_GEX';
        const gexCooldownActive = cooldownActive && !isLiveGex;
        
        if (newGex === null || newGex === undefined) {
          if (gexCooldownActive && currentGex) {
            retainedUpdates.gexLevels = currentGex;
            console.log('[LKG] GEX Levels: Holding previous (incoming null)');
          }
        } else if (typeof newGex === 'object') {
          // SESSION 66: For LIVE GEX, don't apply LKG merge - use fresh data directly
          if (isLiveGex) {
            retainedUpdates.gexLevels = newGex;
            console.log(`🚀 [GEX LIVE] Accepting fresh GEX: net_gex_cr=${newGex.net_gex_cr}`);
          } else {
            retainedUpdates.gexLevels = deepMergeWithLKG(currentGex, newGex, gexCooldownActive);
            
            // CRITICAL: Protect net_gex_cr from dropping to 0
            if (isInvalidValue(retainedUpdates.gexLevels?.net_gex_cr) && 
                !isInvalidValue(currentGex?.net_gex_cr) && gexCooldownActive) {
              retainedUpdates.gexLevels.net_gex_cr = currentGex.net_gex_cr;
              console.log(`[LKG] Net GEX: Holding ${currentGex.net_gex_cr}`);
            }
            
            // SESSION 59: Protect net_gex_cr from SUSPICIOUS DEVIATION (0.4Cr bug)
            if (typeof retainedUpdates.gexLevels?.net_gex_cr === 'number' && 
                typeof currentGex?.net_gex_cr === 'number' && gexCooldownActive) {
              if (hasSuspiciousDeviation(retainedUpdates.gexLevels.net_gex_cr, currentGex.net_gex_cr)) {
                retainedUpdates.gexLevels.net_gex_cr = currentGex.net_gex_cr;
                console.log(`[LKG] Net GEX: Deviation filter → Holding ${currentGex.net_gex_cr}`);
              }
            }
            
            // Protect wall values
            ['put_wall', 'call_wall', 'put_wall_gex', 'call_wall_gex', 'zero_gamma'].forEach(field => {
              if (isInvalidValue(retainedUpdates.gexLevels?.[field]) && 
                  !isInvalidValue(currentGex?.[field]) && gexCooldownActive) {
                retainedUpdates.gexLevels[field] = currentGex[field];
              }
            });
          }
        }
      }
      
      // === 3. PRICE PROTECTION (futuresPrice, spotPrice, ltp) ===
      ['futuresPrice', 'spotPrice', 'ltp'].forEach(priceField => {
        if (retainedUpdates[priceField] !== undefined) {
          const newPrice = retainedUpdates[priceField];
          const currentPrice = currentData[priceField];
          
          // Zero-rejection: Don't let price drop to 0 if we had a valid price
          if (isInvalidValue(newPrice) && !isInvalidValue(currentPrice) && currentPrice > 0) {
            retainedUpdates[priceField] = currentPrice;
            console.log(`[LKG] ${priceField}: Holding ${currentPrice}`);
          }
        }
      });
      
      // === 4. SESSION STATS PROTECTION (Volume only - CVD flows from backend SSOT) ===
      // SESSION 120: CVD now flows directly from backend SSOT without client-side filtering
      if (retainedUpdates.sessionStats !== undefined) {
        const newStats = retainedUpdates.sessionStats;
        const currentStats = currentData.sessionStats;
        
        if (typeof newStats === 'object' && newStats !== null) {
          retainedUpdates.sessionStats = deepMergeWithLKG(currentStats, newStats, cooldownActive);
          
          // SESSION 120: REMOVED CVD protection that was causing anchoring
          // The backend is now the Single Source of Truth for CVD
          // If CVD looks wrong, debug the backend, don't mask it here
        }
      }
      
      // === 5. STRATEGY PULSE PROTECTION ===
      if (retainedUpdates.strategyPulse !== undefined) {
        const newPulse = retainedUpdates.strategyPulse;
        const currentPulse = currentData.strategyPulse;
        
        if (newPulse === null || newPulse === undefined) {
          if (cooldownActive && currentPulse) {
            retainedUpdates.strategyPulse = currentPulse;
          }
        } else if (typeof newPulse === 'object') {
          retainedUpdates.strategyPulse = deepMergeWithLKG(currentPulse, newPulse, cooldownActive);
        }
      }
      
      return {
        ...prev,
        [symbol]: {
          ...currentData,
          ...retainedUpdates,
          lastUpdated: Date.now(),
        },
      };
    });
  }, []); // No dependencies - uses functional update pattern
  
  // === NORMALIZE SYMBOL (map backend names to frontend store keys) ===
  const normalizeSymbol = useCallback((rawSymbol) => {
    if (!rawSymbol) return 'NIFTY';
    const upper = rawSymbol.toUpperCase();
    // Map backend variations to store keys
    // Handle TrueData futures format (NIFTY-I, BANKNIFTY-I)
    if (upper === 'NIFTY-I' || upper === 'NIFTY-II' || upper === 'NIFTY' || upper === 'NIFTY FUT') return 'NIFTY';
    if (upper === 'BANKNIFTY-I' || upper === 'BANKNIFTY-II' || upper === 'BNF' || upper === 'BANKNIFTY FUT' || upper === 'BANKNIFTY') return 'BANKNIFTY';
    // SESSION 136: Reject sector indices — they must NOT map to NIFTY/BANKNIFTY
    // "NIFTY IT", "NIFTY ENERGY" etc. contain "NIFTY" but are NOT NIFTY futures
    if (upper.includes(' ')) return null;  // Any symbol with a space is NOT a futures symbol
    return null;
  }, []);
  
  // === PROCESS INCOMING WEBSOCKET MESSAGE ===
  // STRICT FREEZE: When market is closed, ignore ALL data updates except status messages
  const processMessage = useCallback((message) => {
    const { type, data } = message;
    
    // === SAFETY CHECK: Ensure we have a valid message ===
    if (!type) {
      console.warn('[MarketProvider] Received message with no type:', message);
      return;
    }
    
    // STRICT FREEZE CHECK: Only allow certain messages when market is closed
    // SESSION 66 FIX: Allow gex_levels through even when market closed (replay mode)
    // SESSION 130 FIX: Allow strategy_pulse through for replay mode outside market hours
    const alwaysAllowedTypes = ['init', 'status', 'gex_levels', 'god_score', 'candle', 'tick', 'ltp', 'strategy_pulse'];
    if (!isMarketOpenRef.current && !alwaysAllowedTypes.includes(type)) {
      // Market is closed - silently ignore this data update
      return;
    }
    
    // Extract and normalize symbol from message
    const rawSymbol = data?.symbol || 'NIFTY';
    const msgSymbol = normalizeSymbol(rawSymbol);
    
    // DEBUG: Log incoming data to verify backend is sending non-zero values
    if (type === 'god_score' || type === 'strategy_pulse') {
      console.log(`📥 [MarketProvider] ${type}:`, JSON.stringify(data).slice(0, 200));
    }
    
    switch (type) {
      case 'init': {
        // Initial connection data - respect market status
        // Safety check: ensure data exists before accessing properties
        if (!data) {
          console.warn('[MarketProvider] Received init message with no data');
          break;
        }
        console.log('📥 [MarketProvider] INIT received:', data);
        
        // SESSION 86r: HARD OVERRIDE for REPLAY mode
        // Check if we're in replay mode FIRST before checking is_market_open
        const isReplayMode = data.is_replay_mode || 
                             data.mode === 'replay' || 
                             data.mode === 'TD_REPLAY' ||
                             data.locked_mode === 'REPLAY' ||
                             data.locked_mode === 'TD_REPLAY';
        
        // CRITICAL: Force market "open" if in replay mode - ignore real-world weekend
        const rawMarketOpen = data.is_market_open ?? data.market_status?.is_open ?? false;
        const isMarketOpen = isReplayMode ? true : rawMarketOpen;
        
        isMarketOpenRef.current = isMarketOpen;  // Update ref immediately
        setConnectionStatus(prev => ({
          ...prev,
          // Only show 'live' if market is actually open
          status: isReplayMode ? 'replay' : (isMarketOpen ? 'live' : 'market_closed'),
          isConnected: true,
          dataSource: data.data_source || 'live',
          isMarketOpen: isMarketOpen,
        }));
        
        // SESSION 86i: Capture CVD Anchor from INIT message
        // The cumulative_delta in INIT represents the day's CVD BEFORE the HFT engine started
        // This is the anchor we add session CVD to for true absolute CVD
        const initCvd = data.cumulative_delta ?? data.sessionStats?.cumulative_delta;
        if (initCvd !== undefined && initCvd !== null && initCvd !== 0) {
          console.log(`📌 [CVD ANCHOR] Setting initial anchor from INIT: NIFTY=${initCvd}`);
          setStore(prev => {
            const niftyData = prev.NIFTY || createDefaultSymbolData();
            const bankniftyData = prev.BANKNIFTY || createDefaultSymbolData();
            
            // Only set anchor if not already set
            return {
              ...prev,
              NIFTY: {
                ...niftyData,
                cvd_anchor: niftyData.cvd_anchor ?? initCvd,
                sessionStats: {
                  ...(niftyData.sessionStats || {}),
                  cumulative_delta: initCvd,
                },
              },
              BANKNIFTY: {
                ...bankniftyData,
                // BNF may have different anchor - check for bnf_cumulative_delta
                cvd_anchor: bankniftyData.cvd_anchor ?? (data.bnf_cumulative_delta ?? initCvd * 0.5),
              },
            };
          });
        }
        
        // === VIX Data from init message (Session 47) ===
        // Store VIX data immediately when WebSocket connects so UI shows correct state
        if (data.vix_value !== undefined || data.vix_is_stale !== undefined) {
          console.log('📊 [MarketProvider] Init VIX data:', { vix_value: data.vix_value, vix_is_stale: data.vix_is_stale });
          setStore(prev => {
            const niftyData = prev.NIFTY || createDefaultSymbolData();
            const bankniftyData = prev.BANKNIFTY || createDefaultSymbolData();
            
            const vixFields = {
              vix_value: data.vix_value,
              vix_regime: data.vix_regime,
              vix_is_stale: data.vix_is_stale,
              vix_last_updated: data.vix_last_updated,
            };
            
            return {
              ...prev,
              NIFTY: {
                ...niftyData,
                godScore: { ...(niftyData.godScore || {}), ...vixFields },
              },
              BANKNIFTY: {
                ...bankniftyData,
                godScore: { ...(bankniftyData.godScore || {}), ...vixFields },
              },
            };
          });
        }
        
        if (!isMarketOpen) {
          console.log('🛑 [MarketProvider] STRICT FREEZE ACTIVE - Market closed, all data updates will be ignored');
        }
        break;
      }
      
      case 'status': {
        // Status update from server
        // Safety check: ensure data exists before accessing properties
        if (!data) {
          console.warn('[MarketProvider] Received status message with no data');
          break;
        }
        setConnectionStatus(prev => {
          // SESSION 86r: HARD OVERRIDE for REPLAY mode
          const isReplayMode = data.is_replay_mode || 
                               data.mode === 'replay' || 
                               data.mode === 'TD_REPLAY' ||
                               data.locked_mode === 'REPLAY' ||
                               data.locked_mode === 'TD_REPLAY';
          
          // Force market "open" if in replay - ignore real-world clock
          const rawMarketOpen = data.is_market_open ?? data.market_status?.is_open ?? prev.isMarketOpen;
          const isMarketOpen = isReplayMode ? true : rawMarketOpen;
          
          isMarketOpenRef.current = isMarketOpen;  // Update ref immediately
          return {
            ...prev,
            status: isReplayMode ? 'replay' : (isMarketOpen ? (data.status || prev.status) : 'market_closed'),
            dataSource: data.data_source || prev.dataSource,
            isMarketOpen: isMarketOpen,
          };
        });
        break;
      }
      
      case 'hft_candle': {
        const symbol = normalizeSymbol(data.symbol);
        if (!symbol) break;
        
        // Update HFT candles
        setStore(prev => {
          const current = prev[symbol] || createDefaultSymbolData();
          const hftCandles = [...(current.hftCandles || [])];
          
          // Add or update candle
          const idx = hftCandles.findIndex(c => c.time === data.time);
          if (idx >= 0) {
            hftCandles[idx] = data;
          } else {
            hftCandles.push(data);
            if (hftCandles.length > 300) hftCandles.shift();
          }
          
          // === SESSION 118: CVD FLOWS FREELY FROM BACKEND SSOT ===
          // The backend now uses get_display_cvd() as Single Source of Truth.
          // No frontend firewall - we trust the backend completely.
          // If CVD looks wrong, debug the backend, don't mask it here.
          const incomingCvd = data.session_cvd_absolute ?? data.cvd ?? 0;
          
          // Update session stats from HFT candle - CVD flows directly from backend
          const sessionStats = {
            ...current.sessionStats,
            cumulative_delta: incomingCvd,
            session_volume: data.session_volume ?? current.sessionStats.session_volume,
            vwap: data.vwap ?? current.sessionStats.vwap,
          };
          
          // Build tape entry
          const tapeEntry = {
            time: new Date(data.time * 1000).toLocaleTimeString('en-IN', { 
              hour12: false, timeZone: 'Asia/Kolkata',
              hour: '2-digit', minute: '2-digit', second: '2-digit'
            }),
            price: data.close,
            delta: data.delta,
            velocity: data.velocity,
            sweep: data.has_sweep ? `⚡${data.sweep_direction}` : '',
            signal: data.signal,
            signal_state: data.signal_state,
            prob: data.probability,
          };
          
          const tape = [tapeEntry, ...(current.tape || [])].slice(0, 100);
          
          return {
            ...prev,
            [symbol]: {
              ...current,
              hftCandles,
              sessionStats,
              tape,
              latestCandle: data,
              futuresPrice: data.close || current.futuresPrice,
              lastUpdated: Date.now(),
            },
          };
        });
        break;
      }
      
      case 'candle': {
        const symbol = normalizeSymbol(data.symbol);
        if (!symbol) break;
        // Normalize timestamp: convert seconds to seconds (lightweight-charts expects seconds)
        const normalizedTime = data.time && data.time > 10000000000 
          ? Math.floor(data.time / 1000) // Convert ms to seconds
          : data.time;
        const normalizedCandle = { ...data, time: normalizedTime };
        
        setStore(prev => {
          const current = prev[symbol] || createDefaultSymbolData();
          let candles = [...(current.candles || [])];
          
          const idx = candles.findIndex(c => c.time === normalizedTime);
          if (idx >= 0) {
            candles[idx] = normalizedCandle;
          } else {
            candles.push(normalizedCandle);
            // Sort by time to fix "data must be asc ordered" error
            candles.sort((a, b) => a.time - b.time);
            if (candles.length > 500) candles.shift();
          }
          
          return {
            ...prev,
            [symbol]: { ...current, candles, latestCandle: normalizedCandle, lastUpdated: Date.now() },
          };
        });
        break;
      }
      
      // Periodic state update (3s interval for continuous UI updates)
      case 'state_update': {
        const symbol = normalizeSymbol(data.symbol);
        if (!symbol) break;
        setStore(prev => {
          const current = prev[symbol] || createDefaultSymbolData();
          return {
            ...prev,
            [symbol]: {
              ...current,
              cvd: data.cvd ?? current.cvd,
              sessionVolume: data.session_volume ?? current.sessionVolume,
              velocity: data.velocity ?? current.velocity,
              futuresPrice: (data.latest_price && data.latest_price > 0) ? data.latest_price : current.futuresPrice,
              godScore: data.god_score ?? current.godScore,
              // Update sessionStats.cumulative_delta for top bar display
              sessionStats: {
                ...current.sessionStats,
                cumulative_delta: data.cvd ?? current.sessionStats?.cumulative_delta ?? 0,
                session_volume: data.session_volume ?? current.sessionStats?.session_volume ?? 0,
              },
              lastUpdated: Date.now(),
            },
          };
        });
        break;
      }
      
      // ===================================================================
      // SESSION 55 TWO-LANE PROTOCOL: STRICT SPOT/FUTURES SEPARATION
      // ===================================================================
      // TRADE_UPDATE = FUTURES prices ONLY (NIFTY-I, BANKNIFTY-I)
      // SPOT_UPDATE = SPOT INDEX prices ONLY (NIFTY 50, NIFTY BANK)
      // These MUST NEVER cross-contaminate each other's state!
      // ===================================================================
      
      case 'TRADE_UPDATE':  // New explicit event type
      case 'ltp': {         // Legacy compatibility
        // STRICT: FUTURES price ONLY - updates futLTP/futuresPrice
        // SESSION 136: Must resolve to exact NIFTY or BANKNIFTY — reject unknown symbols
        const symbol = normalizeSymbol(data.symbol);
        if (!symbol) break;  // Reject sector indices and unknown symbols
        const price = typeof data === 'object' ? data.price : data;
        // Only update if price is valid (non-zero) - preserves last known price when feed stops
        if (price && price > 0) {
          // STRICT TWO-LANE: ONLY set futuresPrice, NEVER touch spotPrice
          updateSymbolData(symbol, { ltp: data, futuresPrice: price });
          console.log(`🔵 [TRADE_UPDATE] ${symbol} FUT → ${price}`);
        }
        break;
      }
      
      case 'SPOT_UPDATE':   // New explicit event type
      case 'spot_index': {  // Legacy compatibility
        // STRICT: SPOT INDEX price ONLY - updates spotLTP/spotPrice
        const symbol = normalizeSymbol(data.symbol) || activeSymbolRef.current;
        const price = typeof data === 'object' ? data.price : data;
        if (price && price > 0) {
          // STRICT TWO-LANE: ONLY set spotPrice, NEVER touch futuresPrice
          updateSymbolData(symbol, { spotPrice: price });
          console.log(`🟡 [SPOT_UPDATE] ${symbol} SPOT → ${price}`);
        }
        break;
      }
      
      case 'gex_levels': {
        const symbol = normalizeSymbol(data.symbol);
        if (!symbol) break;
        // SESSION 66: Log GEX updates for debugging
        console.log(`🚀 [GEX UPDATE] ${symbol}: net_gex_cr=${data.net_gex_cr}Cr`);
        // SESSION 55 STRICT: Only update prices if NOT null (null = no real data)
        const updates = { gexLevels: data };
        // Spot price - null means no real data (don't update state)
        if (data.spot_price !== null && data.spot_price > 0) {
          updates.spotPrice = data.spot_price;
        }
        // Futures price - always present from futures feed
        if (data.futures_price !== null && data.futures_price > 0) {
          updates.futuresPrice = data.futures_price;
        }
        updateSymbolData(symbol, updates);
        break;
      }
      
      case 'god_score': {
        // God score applies to active symbol (or specified symbol)
        const symbol = normalizeSymbol(data.symbol) || activeSymbolRef.current;
        
        // === ZOMBIE DETECTION: Log PID changes ===
        if (data.debug_pid) {
          const prevPid = window.__lastGodScorePid;
          if (prevPid && prevPid !== data.debug_pid) {
            console.error(`[ZOMBIE ALERT] PID changed: ${prevPid} → ${data.debug_pid}`);
          }
          window.__lastGodScorePid = data.debug_pid;
        }
        
        // CRITICAL FIX (Session 40): Check if this is a "minimal" WebSocket god_score
        // Minimal ones have total_score but no VIX - preserve VIX from existing
        if (!data.vix_value && data.total_score !== undefined) {
          // Minimal WebSocket message - merge to preserve VIX
          setStore(prev => {
            const currentData = prev[symbol] || createDefaultSymbolData();
            const existingGodScore = currentData.godScore || {};
            
            return {
              ...prev,
              [symbol]: {
                ...currentData,
                godScore: {
                  ...existingGodScore,  // Preserve VIX fields
                  ...data,  // Override with new data
                  // Explicitly keep VIX fields from existing if not in new data
                  vix_value: data.vix_value ?? existingGodScore.vix_value,
                  vix_regime: data.vix_regime ?? existingGodScore.vix_regime,
                  vix_is_stale: data.vix_is_stale ?? existingGodScore.vix_is_stale,
                  threshold: data.threshold ?? existingGodScore.threshold,
                  threshold_reason: data.threshold_reason ?? existingGodScore.threshold_reason,
                },
                lastUpdated: Date.now(),
              }
            };
          });
        } else {
          // Full API response with VIX - use directly
          updateSymbolData(symbol, { godScore: data });
        }
        break;
      }
      
      case 'strategy_pulse': {
        const pulseData = data.data || data;
        const symbol = normalizeSymbol(pulseData.symbol) || activeSymbolRef.current;
        
        // CRITICAL FIX (Session 40): Preserve VIX data from API poll
        // The strategy_pulse doesn't include VIX, so we need to merge carefully
        // SESSION 67b: Don't overwrite persisted godScore with live zeros when market is closed
        setStore(prev => {
          const currentData = prev[symbol] || createDefaultSymbolData();
          const existingGodScore = currentData.godScore || {};
          const existingSessionStats = currentData.sessionStats || {};
          const existingHftBreakdown = existingGodScore.hft_breakdown || {};
          
          // ============================================================
          // SESSION 98c: SIMPLIFIED CVD - Backend is Single Source of Truth
          // ============================================================
          // The backend now sends session_cvd_absolute which is THE correct CVD value.
          // No anchor + session math needed anymore - just use what backend sends.
          // This works consistently for both LIVE and REPLAY modes.
          // ============================================================
          
          // Extract CVD directly from backend (single source of truth)
          const backendCvd = pulseData.hft_breakdown?.session_cvd_absolute 
            ?? pulseData.hft_breakdown?.cvd 
            ?? pulseData.cvd
            ?? currentData.sessionStats?.cumulative_delta
            ?? 0;
          
          // SESSION 98c: Just use the backend CVD directly
          const trueAbsoluteCvd = backendCvd;
          
          // Keep live_session_cvd for backwards compatibility (same as trueAbsoluteCvd now)
          const updatedLiveSessionCvd = backendCvd;
          
          // SESSION 86h: Same preservation logic for delta_slope
          const rawDeltaSlope = pulseData.hft_breakdown?.delta_slope;
          const hasDeltaSlope = rawDeltaSlope !== undefined && rawDeltaSlope !== null;
          const updatedDeltaSlope = hasDeltaSlope 
            ? rawDeltaSlope 
            : currentData.live_delta_slope;
          
          // SESSION 98c: Update sessionStats with backend CVD directly
          // SESSION 121: Also include lee_ready_cvd for top bar display
          // SESSION 127: Also include aggressor_cvd for comparison display
          const leeReadyCvd = pulseData.hft_breakdown?.lee_ready_cvd ?? existingSessionStats?.lee_ready_cvd ?? 0;
          const aggressorCvd = pulseData.hft_breakdown?.aggressor_cvd_display ?? pulseData.hft_breakdown?.aggressor_cvd ?? existingSessionStats?.aggressor_cvd ?? 0;
          const hasValidCvd = backendCvd !== 0 || (pulseData.hft_breakdown?.session_cvd_absolute !== undefined);
          const updatedSessionStats = hasValidCvd ? {
            ...existingSessionStats,
            cumulative_delta: trueAbsoluteCvd,  // Direct from backend
            lee_ready_cvd: leeReadyCvd,  // SESSION 121: Lee-Ready CVD for comparison
            aggressor_cvd: aggressorCvd,  // SESSION 127: Aggressor CVD for comparison
          } : existingSessionStats;
          
          // SESSION 67b: If godScore is persisted AND incoming score is MISSING (undefined), don't overwrite
          // This prevents the live zeros from overwriting historical persisted data
          // SESSION 86n: FIX - 0 is a valid score! Only skip if god_score is literally undefined/null
          const isPersistedData = existingGodScore.is_persisted === true;
          const isIncomingMissing = pulseData.god_score === undefined || pulseData.god_score === null;
          
          // SESSION 86n: Only skip update if we have persisted data AND incoming score is truly MISSING
          // A real score of 0 should update - it's a valid market state
          if (isPersistedData && isIncomingMissing) {
            // Don't overwrite persisted data with zeros - just update timestamp and CVD
            return {
              ...prev,
              [symbol]: {
                ...currentData,
                strategyPulse: pulseData,
                sessionStats: updatedSessionStats,
                // SESSION 98c: Simplified - backend is single source of truth
                live_session_cvd: updatedLiveSessionCvd,
                live_cvd: trueAbsoluteCvd,  // Direct from backend
                live_delta_slope: updatedDeltaSlope,
                lastUpdated: Date.now(),
              }
            };
          }
          
          // Build merged godScore that preserves VIX fields
          // SESSION 86e: Also include hft_breakdown with CVD for Order Flow panel
          // SESSION 86h: Preserve existing hft_breakdown values if incoming doesn't have them
          // SESSION 98c: Store backend CVD directly in hft_breakdown
          const hasValidBackendCvd = backendCvd !== 0 || (pulseData.hft_breakdown?.session_cvd_absolute !== undefined);
          const mergedHftBreakdown = pulseData.hft_breakdown ? {
            ...existingHftBreakdown,
            ...pulseData.hft_breakdown,
            // SESSION 98c: Use backend CVD directly (single source of truth)
            session_cvd_absolute: hasValidBackendCvd 
              ? trueAbsoluteCvd  // Use backend CVD directly
              : existingHftBreakdown.session_cvd_absolute,
            cvd: hasValidBackendCvd 
              ? trueAbsoluteCvd  // Use backend CVD directly
              : existingHftBreakdown.cvd,
            delta_slope: hasDeltaSlope 
              ? rawDeltaSlope 
              : existingHftBreakdown.delta_slope,
          } : existingHftBreakdown;
          
          // ============================================================
          // SESSION 86r: EXPLICIT FIELD EXTRACTION (NO DEEP SPREADING)
          // ============================================================
          // The React deep merge was dropping values. Force explicit mapping.
          // ============================================================
          
          // EXPLICIT: God Score (handles 0 as valid, handles object vs primitive)
          const rawGodScore = pulseData.god_score ?? pulseData.godScore;
          const hasValidGodScore = rawGodScore !== undefined && rawGodScore !== null;
          const godScoreValue = typeof rawGodScore === 'object' 
            ? (rawGodScore.total_score ?? rawGodScore.score ?? 0)
            : (hasValidGodScore ? rawGodScore : (existingGodScore.total_score ?? 0));
          
          // EXPLICIT: CVD - prioritize top-level, fallback to breakdown
          const explicitCvd = pulseData.cvd ?? 
                              pulseData.live_cvd ?? 
                              pulseData.hft_breakdown?.cvd ?? 
                              pulseData.hft_breakdown?.session_cvd_absolute ??
                              trueAbsoluteCvd;
          
          // EXPLICIT: Delta slope
          const explicitDelta = pulseData.delta ?? 
                                pulseData.delta_slope ??
                                pulseData.hft_breakdown?.delta_slope ??
                                updatedDeltaSlope;
          
          // EXPLICIT: Velocity
          const explicitVelocity = pulseData.velocity ?? 
                                   pulseData.hft_breakdown?.is_high_velocity ??
                                   currentData.velocity ?? false;
          
          // Build god score object explicitly (no blind spread)
          const finalGodScore = {
            total_score: godScoreValue,
            hft_score: pulseData.components?.hft ?? existingGodScore.hft_score ?? 0,
            gamma_score: pulseData.components?.gamma ?? existingGodScore.gamma_score ?? 0,
            profile_score: pulseData.components?.profile ?? existingGodScore.profile_score ?? 0,
            order_flow_score: pulseData.session_79?.order_flow_score ?? existingGodScore.order_flow_score ?? 0,
            liquidity_score: pulseData.session_79?.liquidity_score ?? existingGodScore.liquidity_score ?? 0,
            gex_score: pulseData.session_79?.gex_score ?? existingGodScore.gex_score ?? 0,
            direction: pulseData.direction || existingGodScore.direction,
            hft_breakdown: mergedHftBreakdown,
            // Preserve VIX gate data from existing
            vix_value: existingGodScore.vix_value,
            vix_gate: existingGodScore.vix_gate,
          };
          
          // SESSION 86r: EXPLICIT RETURN - force these specific fields
          return {
            ...prev,
            [symbol]: {
              ...currentData,
              // EXPLICIT: Raw pulse data (for debugging)
              strategyPulse: pulseData,
              // EXPLICIT: God Score (forced, no merge)
              godScore: finalGodScore,
              god_score: godScoreValue,  // Also set top-level for compat
              // EXPLICIT: Session stats
              sessionStats: updatedSessionStats,
              // EXPLICIT: CVD fields - SESSION 98c: Backend is single source
              live_session_cvd: updatedLiveSessionCvd,
              live_cvd: explicitCvd,
              cvd: explicitCvd,  // Also set top-level cvd
              // EXPLICIT: Delta (forced)
              live_delta_slope: explicitDelta,
              delta: explicitDelta,
              delta_slope: explicitDelta,
              // EXPLICIT: Velocity
              velocity: explicitVelocity,
              // EXPLICIT: Timestamp
              lastUpdated: Date.now(),
            }
          };
        });
        break;
      }
      
      case 'hft_signal': {
        const symbol = normalizeSymbol(data.symbol) || activeSymbolRef.current;
        updateSymbolData(symbol, { hftSignal: data });
        break;
      }
      
      case 'trident_status': {
        updateSymbolData(activeSymbolRef.current, { tridentStatus: data });
        break;
      }
      
      case 'market_profile': {
        const symbol = normalizeSymbol(data.symbol) || activeSymbolRef.current;
        updateSymbolData(symbol, { 
          marketProfile: data,
          dpocLineData: data.dpoc_line || [],
        });
        break;
      }
      
      case 'poc_line': {
        updateSymbolData(activeSymbolRef.current, { pocLineData: data });
        break;
      }
      
      case 'trading_stats': {
        updateSymbolData(activeSymbolRef.current, { tradingStats: data });
        break;
      }
      
      case 'trade_event': {
        setStore(prev => {
          const current = prev[activeSymbolRef.current];
          const tradeEvents = [...(current.tradeEvents || []), data].slice(-100);
          return { ...prev, [activeSymbolRef.current]: { ...current, tradeEvents } };
        });
        break;
      }
      
      case 'alert': {
        setStore(prev => {
          const current = prev[activeSymbolRef.current];
          const alerts = [...(current.alerts || []), data].slice(-100);
          return { ...prev, [activeSymbolRef.current]: { ...current, alerts } };
        });
        break;
      }
      
      // SESSION 70: Bimodal Exit Event - Real-time position close notification
      case 'bimodal_exit': {
        const exitData = data.data || data;
        const symbol = normalizeSymbol(exitData.symbol) || activeSymbolRef.current;
        
        // Clear the position_state when an exit is received
        setStore(prev => {
          const currentData = prev[symbol] || {};
          const currentPulse = currentData.strategyPulse || {};
          const currentBimodal = currentPulse.bimodal || {};
          
          // Update bimodal data to clear position_state and record exit
          const updatedBimodal = {
            ...currentBimodal,
            position_state: null,  // Clear position - it's closed
            last_exit: {
              reason: exitData.exit_reason,
              exit_price: exitData.exit_price,
              entry_price: exitData.entry_price,
              pnl: exitData.pnl,
              exit_type: exitData.exit_type,
              timestamp: exitData.timestamp
            }
          };
          
          return {
            ...prev,
            [symbol]: {
              ...currentData,
              strategyPulse: {
                ...currentPulse,
                bimodal: updatedBimodal
              },
              lastBimodalExit: exitData,  // Keep full exit data for UI access
              lastUpdated: Date.now()
            }
          };
        });
        
        // Log the exit for debugging
        console.log(`[BIMODAL EXIT] ${symbol}: ${exitData.exit_reason} | PnL: ${exitData.pnl}`);
        break;
      }
      
      // ============================================
      // SESSION 86: REPLAY TICK HANDLER
      // SESSION 86b: Enhanced to update ALL price displays and timestamp
      // ============================================
      // Updates price and god score during replay mode
      // NO REGRESSION: Time Blackout logic remains in backend - we just display the vetoed data
      case 'replay_tick': {
        const tickData = data.data || data;
        const symbol = normalizeSymbol(tickData.symbol) || activeSymbolRef.current;
        const price = tickData.price || tickData.futures_price;
        const wsTimestamp = tickData.timestamp;
        
        // SESSION 86b: Log every tick for debugging (throttled to every 50th)
        if (Math.random() < 0.02) {
          console.log(`🎬 [REPLAY_TICK] ${symbol} price: ${price}, ts: ${wsTimestamp}`);
        }
        
        // Update futures price for display - EVERY TICK regardless of veto
        if (price && price > 0) {
          setStore(prev => {
            const currentData = prev[symbol] || createDefaultSymbolData();
            const existingStrategyPulse = currentData.strategyPulse || {};
            const existingGodScore = currentData.godScore || {};
            
            // Build god score update if available
            let godScoreUpdate = existingGodScore;
            const gs = tickData.god_score;
            if (gs) {
              godScoreUpdate = {
                ...existingGodScore,
                total_score: gs.total_score ?? existingGodScore.total_score,
                ema_score: gs.ema_score ?? existingGodScore.ema_score,
                hft_score: gs.hft_score ?? existingGodScore.hft_score,
                gamma_score: gs.gamma_score ?? existingGodScore.gamma_score,
                profile_score: gs.profile_score ?? existingGodScore.profile_score,
                direction: gs.direction ?? existingGodScore.direction,
                signal_state: gs.signal_state ?? existingGodScore.signal_state,
                is_vetoed: gs.is_vetoed ?? existingGodScore.is_vetoed,
                veto_reason: gs.veto_reason ?? existingGodScore.veto_reason,
              };
            }
            
            return {
              ...prev,
              [symbol]: {
                ...currentData,
                // Update all price fields
                futuresPrice: price,
                ltp: { price, timestamp: wsTimestamp },
                // Update strategyPulse with app_mode, price, and WebSocket timestamp
                strategyPulse: {
                  ...existingStrategyPulse,
                  app_mode: tickData.app_mode || 'REPLAY',
                  god_score: gs?.total_score ?? existingStrategyPulse.god_score,
                  timestamp: wsTimestamp,  // SESSION 86b: Use WebSocket timestamp
                  // Update market_data.price for Command Center
                  market_data: {
                    ...(existingStrategyPulse.market_data || {}),
                    price: price,
                  },
                  // Include veto status from replay
                  is_vetoed: gs?.is_vetoed ?? existingStrategyPulse.is_vetoed,
                  veto_reason: gs?.veto_reason ?? existingStrategyPulse.veto_reason,
                },
                godScore: godScoreUpdate,
                lastUpdated: Date.now(),
              }
            };
          });
        }
        break;
      }
      
      default:
        // Unknown message type
        break;
    }
  // SESSION 86j: CRITICAL FIX - Remove activeSymbol from dependencies
  // Using activeSymbol in deps caused WebSocket reconnection loops because:
  // activeSymbol changes → processMessage recreates → queueMessage recreates → connect recreates → WS reconnects
  // SOLUTION: Use activeSymbolRef.current inside handler, not activeSymbol directly
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSymbolData, normalizeSymbol]);
  
  // === BATCHED MESSAGE PROCESSOR ("BUTTER FIX") ===
  // Flushes the message buffer every 100ms, taking LATEST values for price/score
  // and APPENDING all signals/alerts to prevent data loss
  const flushMessageBuffer = useCallback(() => {
    const buffer = messageBufferRef.current;
    if (buffer.length === 0) {
      isThrottledRef.current = false;
      return;
    }
    
    // Group messages by type, keeping only LATEST for high-frequency data
    const latestByType = {};
    const signalsToAppend = [];
    const alertsToAppend = [];
    
    // Process buffer - keep latest of each type, accumulate signals/alerts
    buffer.forEach(msg => {
      const { type, data } = msg;
      
      // APPEND mode for signals and alerts (don't lose any)
      if (type === 'alert') {
        alertsToAppend.push(data);
      } else if (type === 'trade_event') {
        signalsToAppend.push(data);
      } else {
        // LATEST mode for everything else (price, candles, god_score, etc.)
        // Use symbol as part of key to track per-symbol updates
        const symbol = data?.symbol || 'DEFAULT';
        const key = `${type}_${symbol}`;
        latestByType[key] = msg;
      }
    });
    
    // Clear buffer
    messageBufferRef.current = [];
    isThrottledRef.current = false;
    
    // Process latest messages (ONE state update per type)
    Object.values(latestByType).forEach(msg => {
      processMessage(msg);
    });
    
    // Batch append all accumulated alerts
    // SESSION 86j: Use activeSymbolRef.current to avoid dependency chain
    if (alertsToAppend.length > 0) {
      setStore(prev => {
        const currentSymbol = activeSymbolRef.current;
        const current = prev[currentSymbol];
        const alerts = [...(current.alerts || []), ...alertsToAppend].slice(-100);
        return { ...prev, [currentSymbol]: { ...current, alerts } };
      });
    }
    
    // Batch append all accumulated trade events
    if (signalsToAppend.length > 0) {
      setStore(prev => {
        const currentSymbol = activeSymbolRef.current;
        const current = prev[currentSymbol];
        const tradeEvents = [...(current.tradeEvents || []), ...signalsToAppend].slice(-100);
        return { ...prev, [currentSymbol]: { ...current, tradeEvents } };
      });
    }
    
    // Log batching stats periodically
    if (Math.random() < 0.01) { // 1% chance to log
      console.log(`[MarketProvider] 🧈 BUTTER: Batched ${buffer.length} msgs → ${Object.keys(latestByType).length} updates`);
    }
  // SESSION 86j: Remove activeSymbol from deps - use ref instead
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processMessage]);
  
  // === QUEUE MESSAGE FOR BATCHING ===
  // Instead of processing immediately, queue and flush every 100ms
  const queueMessage = useCallback((message) => {
    // CRITICAL: Some messages should bypass batching (init, status, force_logout)
    if (message.type === 'init' || message.type === 'status') {
      processMessage(message);
      return;
    }
    
    // SESSION 109: Handle force_logout from WebSocket security heartbeat
    if (message.type === 'force_logout') {
      console.warn('🔐 Force logout received:', message.reason, message.message);
      // Import logout from AuthContext and trigger it
      // Note: logout is handled at the App level via window event
      window.dispatchEvent(new CustomEvent('force_logout', { detail: message }));
      return;
    }
    
    // Add to buffer
    messageBufferRef.current.push(message);
    
    // Start throttle timer if not already running
    if (!isThrottledRef.current) {
      isThrottledRef.current = true;
      throttleTimerRef.current = setTimeout(flushMessageBuffer, BATCH_INTERVAL_MS);
    }
  }, [processMessage, flushMessageBuffer]);
  
  // === REST API POLLING REFS (defined before connect) ===
  const pollingIntervalRef = useRef(null);
  const wsFailCountRef = useRef(0);
  const isPollingRef = useRef(false);
  const startPollingRef = useRef(null);  // Will be set after startPolling is defined
  
  // === WEBSOCKET CONNECTION (SINGLETON - NEVER RECREATED) ===
  const connect = useCallback(() => {
    // SESSION 86r: ALWAYS attempt WebSocket connection regardless of market hours
    // The backend will tell us the mode (REPLAY/LIVE/OFFLINE) and we'll handle it there
    // This fixes the issue where the app would skip connection in off-market hours
    // even when TD_REPLAY mode is active
    
    // Don't reconnect if already connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('[MarketProvider] No auth token, cannot connect');
      return;
    }
    
    // Build WebSocket URL properly for both development (full URL) and production (relative)
    let wsUrl;
    if (API && API.startsWith('http')) {
      // Full URL provided (development/preview mode)
      const wsProtocol = API.startsWith('https') ? 'wss' : 'ws';
      const baseUrl = API.replace(/^https?:\/\//, '');
      wsUrl = `${wsProtocol}://${baseUrl}/api/ws/market-feed?token=${token}`;
    } else {
      // Relative URL (production mode on same domain)
      const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      wsUrl = `${wsProtocol}://${window.location.host}/api/ws/market-feed?token=${token}`;
    }
    console.log('[MarketProvider] Connecting WebSocket to:', wsUrl.substring(0, 80) + '...');
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = async () => {
        console.log('✅ [MarketProvider] WebSocket CONNECTED');
        isConnectedRef.current = true;
        
        // Check feed status (includes replay mode awareness) before declaring LIVE
        try {
          const feedRes = await fetch(`${API}/api/feed/status`);
          const feedData = await feedRes.json();
          // Use is_market_open from feed/status which considers replay mode
          const isMarketOpen = feedData.is_market_open ?? false;
          const isReplayMode = feedData.is_replay_mode ?? false;
          const isFeedConnected = feedData.is_connected ?? false;
          
          // Determine correct status string
          let newStatus = 'market_closed';
          if (isReplayMode) {
            newStatus = isFeedConnected ? 'replay' : 'replay_waiting';
          } else if (isMarketOpen) {
            newStatus = 'live';
          }
          
          // Update connection status based on actual feed status
          setConnectionStatus(prev => ({
            ...prev,
            status: newStatus,
            isConnected: true,  // WebSocket is connected
            isMarketOpen: isMarketOpen,
            wasConnected: true,  // Track that we've connected at least once
          }));
          
          console.log(`📊 [MarketProvider] Feed status: ${isMarketOpen ? 'OPEN' : 'CLOSED'} | Replay: ${isReplayMode} | FeedConnected: ${isFeedConnected} | Status: ${newStatus}`);
        } catch (err) {
          console.warn('[MarketProvider] Failed to check feed status:', err);
          // Default to connected but let server init message determine market status
          setConnectionStatus(prev => ({
            ...prev,
            isConnected: true,
            wasConnected: true,
          }));
        }
        
        // Start ping interval - Keep alive every 15s to prevent Cloudflare timeout
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, 15000);
      };
      
      wsRef.current.onmessage = (event) => {
        // Ignore pong responses
        if (event.data === 'pong') return;
        
        try {
          const message = JSON.parse(event.data);
          // USE BATCHING: Queue message instead of processing immediately
          // This caps render rate at 10 FPS (100ms batches) to prevent UI freeze
          queueMessage(message);
        } catch (err) {
          console.error('[MarketProvider] Parse error:', err);
        }
      };
      
      wsRef.current.onclose = () => {
        console.log('🔴 [MarketProvider] WebSocket DISCONNECTED');
        isConnectedRef.current = false;
        clearInterval(pingIntervalRef.current);
        wsFailCountRef.current++;
        
        // If we've failed even once in replay mode, switch to polling
        if (wsFailCountRef.current >= 1 && !isPollingRef.current && startPollingRef.current) {
          console.log('[MarketProvider] WebSocket failed, switching to polling');
          startPollingRef.current();
        } else {
          // Update status to disconnected only if not polling
          if (!isPollingRef.current) {
            setConnectionStatus(prev => ({
              ...prev,
              status: 'disconnected',
              isConnected: false,
            }));
          }
          
          // Attempt reconnect after 3 seconds - SESSION 86r: Always attempt, server will handle mode
          reconnectTimeoutRef.current = setTimeout(() => {
            if (!isConnectedRef.current) {
              // SESSION 86r: Always try to reconnect - let server tell us the mode
              // The backend will return REPLAY/LIVE/OFFLINE and we handle it there
              connect();
            }
          }, 3000);
        }
      };
      
      wsRef.current.onerror = (err) => {
        console.error('[MarketProvider] WebSocket error:', err);
        wsFailCountRef.current++;
        
        // Start polling on error if we haven't already
        if (wsFailCountRef.current >= 2 && !isPollingRef.current && startPollingRef.current) {
          console.log('[MarketProvider] WebSocket error, starting polling fallback');
          startPollingRef.current();
        }
      };
      
    } catch (err) {
      console.error('[MarketProvider] Connection error:', err);
      wsFailCountRef.current++;
      if (!isPollingRef.current && startPollingRef.current) {
        startPollingRef.current();
      }
    }
  }, [queueMessage]);
  
  // === FETCH INITIAL DATA (REST API) ===
  // SESSION 67b: Enhanced to use /api/market/snapshot for complete persisted data
  const fetchInitialData = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    
    try {
      // First, try to fetch complete market snapshots (includes persisted god_score when market closed)
      // SESSION 121: Also fetch HFT status for SSOT lee_ready_cvd value
      const [niftySnapshot, bankniftySnapshot, niftyHftStatus, bankniftyHftStatus] = await Promise.all([
        fetch(`${API}/api/market/snapshot?symbol=NIFTY`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch(`${API}/api/market/snapshot?symbol=BANKNIFTY`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch(`${API}/api/hft/status?symbol=NIFTY`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch(`${API}/api/hft/status?symbol=BANKNIFTY`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
      ]);
      
      // SESSION 121: Extract SSOT lee_ready_cvd from HFT status (authoritative source)
      const niftyLeeReadyCvd = niftyHftStatus?.lee_ready_cvd ?? 0;
      const bankniftyLeeReadyCvd = bankniftyHftStatus?.lee_ready_cvd ?? 0;
      
      // If snapshots available and persisted, use them (market is closed)
      const niftyIsPersisted = niftySnapshot?.is_persisted;
      const bankniftyIsPersisted = bankniftySnapshot?.is_persisted;
      
      console.log('📦 [MarketProvider] Market Snapshots:', { 
        niftyIsPersisted, 
        bankniftyIsPersisted,
        niftyGodScore: niftySnapshot?.god_score?.total_score,
        bankniftyGodScore: bankniftySnapshot?.god_score?.total_score,
        niftyLeeReadyCvd,
        bankniftyLeeReadyCvd,
      });
      
      // Use snapshot data for NIFTY
      if (niftySnapshot) {
        const niftyGexLevels = niftySnapshot.gex_levels || niftySnapshot.gexLevels || {};
        
        // Add is_persisted flag to gexLevels for banner display
        if (niftyIsPersisted) {
          niftyGexLevels.is_persisted = true;
          niftyGexLevels.persisted_date = niftySnapshot.persisted_date;
          niftyGexLevels.persisted_time = niftySnapshot.persisted_time;
        }
        
        const niftyUpdate = { 
          gexLevels: niftyGexLevels,
          futuresPrice: niftySnapshot.gex_levels?.futures_price || niftySnapshot.price || 0,
          spotPrice: niftySnapshot.gex_levels?.spot_price || 0,
        };
        
        // If persisted, also set godScore and imbalance_zones from snapshot
        if (niftyIsPersisted && niftySnapshot.god_score) {
          niftyUpdate.godScore = {
            ...niftySnapshot.god_score,
            // Add imbalance_zones to godScore for GodScoreCommandCenter
            imbalance_zones: niftySnapshot.imbalance_zones,
            // Add VIX data
            vix_value: niftySnapshot.vix?.vix_value,
            vix_is_stale: niftySnapshot.vix?.vix_is_stale ?? true,
            vix_regime: niftySnapshot.vix?.vix_regime || 'UNAVAILABLE',
            // Add persisted flag for UI
            is_persisted: true,
            persisted_date: niftySnapshot.persisted_date,
            persisted_time: niftySnapshot.persisted_time,
          };
          
          // Set session stats from snapshot
          // SESSION 121: Use SSOT lee_ready_cvd from HFT status (fetched earlier)
          // SESSION 127: Also include aggressor_cvd
          if (niftySnapshot.session_stats) {
            niftyUpdate.sessionStats = {
              cumulative_delta: niftySnapshot.session_stats.cvd || 0,
              lee_ready_cvd: niftyLeeReadyCvd,  // SESSION 121: SSOT from HFT status
              aggressor_cvd: niftyHftStatus?.aggressor_cvd || 0,  // SESSION 127: Aggressor CVD
              session_volume: niftySnapshot.session_stats.session_volume || 0,
              session_buy_volume: niftySnapshot.session_stats.session_buy_volume || 0,
              session_sell_volume: niftySnapshot.session_stats.session_sell_volume || 0,
              vwap: 0,
              tick_count: 0,
            };
          }
          
          console.log('📦 [MarketProvider] NIFTY using PERSISTED godScore:', niftySnapshot.god_score.total_score);
        }
        
        updateSymbolData('NIFTY', niftyUpdate);
      }
      
      // Use snapshot data for BANKNIFTY
      if (bankniftySnapshot) {
        const bankniftyGexLevels = bankniftySnapshot.gex_levels || bankniftySnapshot.gexLevels || {};
        
        // Add is_persisted flag to gexLevels for banner display
        if (bankniftyIsPersisted) {
          bankniftyGexLevels.is_persisted = true;
          bankniftyGexLevels.persisted_date = bankniftySnapshot.persisted_date;
          bankniftyGexLevels.persisted_time = bankniftySnapshot.persisted_time;
        }
        
        const bankniftyUpdate = { 
          gexLevels: bankniftyGexLevels,
          futuresPrice: bankniftySnapshot.gex_levels?.futures_price || bankniftySnapshot.price || 0,
          spotPrice: bankniftySnapshot.gex_levels?.spot_price || 0,
        };
        
        // If persisted, also set godScore and imbalance_zones from snapshot
        if (bankniftyIsPersisted && bankniftySnapshot.god_score) {
          bankniftyUpdate.godScore = {
            ...bankniftySnapshot.god_score,
            imbalance_zones: bankniftySnapshot.imbalance_zones,
            vix_value: bankniftySnapshot.vix?.vix_value,
            vix_is_stale: bankniftySnapshot.vix?.vix_is_stale ?? true,
            vix_regime: bankniftySnapshot.vix?.vix_regime || 'UNAVAILABLE',
            is_persisted: true,
            persisted_date: bankniftySnapshot.persisted_date,
            persisted_time: bankniftySnapshot.persisted_time,
          };
          
          // SESSION 121: Use SSOT lee_ready_cvd from HFT status (fetched earlier)
          // SESSION 127: Also include aggressor_cvd
          if (bankniftySnapshot.session_stats) {
            bankniftyUpdate.sessionStats = {
              cumulative_delta: bankniftySnapshot.session_stats.cvd || 0,
              lee_ready_cvd: bankniftyLeeReadyCvd,  // SESSION 121: SSOT from HFT status
              aggressor_cvd: bankniftyHftStatus?.aggressor_cvd || 0,  // SESSION 127: Aggressor CVD
              session_volume: bankniftySnapshot.session_stats.session_volume || 0,
              session_buy_volume: bankniftySnapshot.session_stats.session_buy_volume || 0,
              session_sell_volume: bankniftySnapshot.session_stats.session_sell_volume || 0,
              vwap: 0,
              tick_count: 0,
            };
          }
          
          console.log('📦 [MarketProvider] BANKNIFTY using PERSISTED godScore:', bankniftySnapshot.god_score.total_score);
        }
        
        updateSymbolData('BANKNIFTY', bankniftyUpdate);
      }
      
      // If market is LIVE (not persisted), fetch additional live data
      if (!niftyIsPersisted || !bankniftyIsPersisted) {
        // Fetch GEX, God Score, and HFT for both symbols in parallel (original logic)
        const [niftyGex, bankniftyGex, niftyGodScore, bankniftyGodScore, niftyHftStatus, bankniftyHftStatus] = await Promise.all([
          !niftyIsPersisted ? fetch(`${API}/api/gex/levels?symbol=NIFTY`, { headers }).then(r => r.ok ? r.json() : null) : Promise.resolve(null),
          !bankniftyIsPersisted ? fetch(`${API}/api/gex/levels?symbol=BANKNIFTY`, { headers }).then(r => r.ok ? r.json() : null) : Promise.resolve(null),
          !niftyIsPersisted ? fetch(`${API}/api/god-score/status?symbol=NIFTY`, { headers }).then(r => r.ok ? r.json() : null) : Promise.resolve(null),
          !bankniftyIsPersisted ? fetch(`${API}/api/god-score/status?symbol=BANKNIFTY`, { headers }).then(r => r.ok ? r.json() : null) : Promise.resolve(null),
          !niftyIsPersisted ? fetch(`${API}/api/hft/status?symbol=NIFTY`, { headers }).then(r => r.ok ? r.json() : null) : Promise.resolve(null),
          !bankniftyIsPersisted ? fetch(`${API}/api/hft/status?symbol=BANKNIFTY`, { headers }).then(r => r.ok ? r.json() : null) : Promise.resolve(null),
      ]);
      
      console.log('📦 [MarketProvider] Initial data fetched:', { 
        niftyGex: !!niftyGex, 
        bankniftyGex: !!bankniftyGex, 
        niftyGodScore: niftyGodScore?.total_score, 
        bankniftyGodScore: bankniftyGodScore?.total_score,
        niftyHftStatus: !!niftyHftStatus,
        bankniftyHftStatus: !!bankniftyHftStatus,
        niftyCVD: niftyHftStatus?.engine?.cvd,
        bankniftyCVD: bankniftyHftStatus?.engine?.cvd,
      });
      
      if (niftyGex) {
        updateSymbolData('NIFTY', { 
          gexLevels: niftyGex,
          futuresPrice: niftyGex.futures_price || 0,
          spotPrice: niftyGex.spot_price || 0,
        });
      }
      
      if (bankniftyGex) {
        updateSymbolData('BANKNIFTY', { 
          gexLevels: bankniftyGex,
          futuresPrice: bankniftyGex.futures_price || 0,
          spotPrice: bankniftyGex.spot_price || 0,
        });
      }
      
      if (niftyGodScore) {
        updateSymbolData('NIFTY', { godScore: niftyGodScore });
      }
      
      // NEW: Also fetch BANKNIFTY God Score on init
      if (bankniftyGodScore) {
        console.log('📊 [MarketProvider] Initial BANKNIFTY God Score:', bankniftyGodScore.total_score);
        updateSymbolData('BANKNIFTY', { godScore: bankniftyGodScore });
      }
      
      // Update NIFTY HFT data
      // STRICT DATA CONTRACT: Use session_cvd_absolute as primary CVD source
      if (niftyHftStatus?.engine) {
        const niftyCvd = niftyHftStatus.engine.session_cvd_absolute || niftyHftStatus.engine.cvd || 0;
        console.log('📊 [MarketProvider] Initial NIFTY CVD:', { session_cvd_absolute: niftyHftStatus.engine.session_cvd_absolute, cvd: niftyHftStatus.engine.cvd, final: niftyCvd });
        updateSymbolData('NIFTY', {
          sessionStats: {
            cumulative_delta: niftyCvd,
            session_volume: niftyHftStatus.engine.session_volume || 0,
            vwap: niftyHftStatus.engine.vwap || 0,
            tick_count: niftyHftStatus.engine.candle_count || 0,
          },
          hftSignal: {
            signal: niftyHftStatus.engine.signal,
            signal_state: niftyHftStatus.engine.signal_state,
            probability: niftyHftStatus.engine.probability,
          },
        });
      }
      
      // Update BANKNIFTY HFT data (NEW - now fetched on init too!)
      // STRICT DATA CONTRACT: Use session_cvd_absolute as primary CVD source
      if (bankniftyHftStatus?.engine) {
        const bnCvd = bankniftyHftStatus.engine.session_cvd_absolute || bankniftyHftStatus.engine.cvd || 0;
        console.log('📊 [MarketProvider] Initial BANKNIFTY CVD:', { session_cvd_absolute: bankniftyHftStatus.engine.session_cvd_absolute, cvd: bankniftyHftStatus.engine.cvd, final: bnCvd });
        updateSymbolData('BANKNIFTY', {
          sessionStats: {
            cumulative_delta: bnCvd,
            session_volume: bankniftyHftStatus.engine.session_volume || 0,
            vwap: bankniftyHftStatus.engine.vwap || 0,
            tick_count: bankniftyHftStatus.engine.candle_count || 0,
          },
          hftSignal: {
            signal: bankniftyHftStatus.engine.signal,
            signal_state: bankniftyHftStatus.engine.signal_state,
            probability: bankniftyHftStatus.engine.probability,
          },
        });
      }
      } // Close the conditional block for live data fetch
    } catch (err) {
      console.error('[MarketProvider] Failed to fetch initial data:', err);
    }
  }, [updateSymbolData]);
  
  // === REST API POLLING (Fallback when WebSocket fails) ===
  const startPolling = useCallback(() => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;
    
    console.log('[MarketProvider] 📡 Starting REST API polling (WebSocket fallback)');
    
    // DO NOT auto-set status to 'live' here - let the actual data determine status
    // This prevents fake "LIVE" status when market is actually closed
    
    // Poll every 2 seconds
    pollingIntervalRef.current = setInterval(async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const headers = { 'Authorization': `Bearer ${token}` };
      
      try {
        // Fetch all data in parallel (including BANKNIFTY-specific data + market status)
        const [
          niftyHftStatus, 
          bankniftyHftStatus,  // NEW: Separate HFT status for BANKNIFTY
          niftyGodScore, 
          bankniftyGodScore, 
          niftyGex, 
          bankniftyGex, 
          niftyProfile, 
          bankniftyProfile,
          feedStatus  // Check feed connection status
        ] = await Promise.all([
          fetch(`${API}/api/hft/status?symbol=NIFTY`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
          fetch(`${API}/api/hft/status?symbol=BANKNIFTY`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
          fetch(`${API}/api/god-score/status?symbol=NIFTY`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
          fetch(`${API}/api/god-score/status?symbol=BANKNIFTY`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
          fetch(`${API}/api/gex/levels?symbol=NIFTY`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
          fetch(`${API}/api/gex/levels?symbol=BANKNIFTY`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
          fetch(`${API}/api/market-profile/status?symbol=NIFTY`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
          fetch(`${API}/api/market-profile/status?symbol=BANKNIFTY`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
          fetch(`${API}/api/feed/status`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
        ]);
        
        // UPDATE CONNECTION STATUS BASED ON ACTUAL FEED STATUS
        if (feedStatus) {
          // SESSION 86r: HARD OVERRIDE - Force market "open" in replay mode
          // This prevents the UI from showing grey/offline when in TD_REPLAY
          const isReplayMode = feedStatus.is_replay_mode || 
                               feedStatus.mode === 'replay' || 
                               feedStatus.mode === 'TD_REPLAY' ||
                               feedStatus.locked_mode === 'REPLAY' ||
                               feedStatus.locked_mode === 'TD_REPLAY';
          
          // CRITICAL: If in REPLAY mode, FORCE is_market_open = true
          // The real-world weekend clock is IRRELEVANT when replaying historical data
          const isMarketOpen = isReplayMode ? true : (feedStatus.is_market_open ?? false);
          const isConnected = feedStatus.is_connected ?? false;
          
          // Determine correct status string
          let newStatus = 'market_closed';
          if (isReplayMode) {
            newStatus = isConnected ? 'replay' : 'replay_waiting';
          } else if (isMarketOpen) {
            newStatus = isConnected ? 'live' : 'disconnected';
          }
          
          setConnectionStatus(prev => ({
            ...prev,
            status: newStatus,
            isConnected: isConnected,
            isMarketOpen: isMarketOpen, // SESSION 86r: Already forced true for replay
            dataSource: 'polling',
          }));
          
          // If market is closed AND not in replay mode, don't update prices
          if (!isMarketOpen && !isReplayMode) {
            console.log('[MarketProvider] Market closed and not in replay - freezing data');
            return; // Skip data updates when market is closed
          }
        }
        
        // Update NIFTY HFT data
        if (niftyHftStatus?.engine) {
          const engine = niftyHftStatus.engine;
          // STRICT DATA CONTRACT: Use session_cvd_absolute as primary CVD source
          const niftyCvd = engine.session_cvd_absolute || engine.cvd || engine.cumulative_delta || niftyHftStatus.cvd || 0;
          updateSymbolData('NIFTY', {
            sessionStats: {
              cumulative_delta: niftyCvd,
              session_volume: engine.session_volume || niftyHftStatus.session_volume || 0,
              vwap: engine.vwap || 0,
              tick_count: engine.candle_count || 0,
            },
            hftSignal: {
              signal: engine.signal,
              signal_state: engine.signal_state,
              probability: engine.probability,
              velocity: engine.velocity,
              delta: engine.tick_delta || engine.delta,  // Per-candle delta
            },
            futuresPrice: engine.last_price || 0,
          });
        }
        
        // Update BANKNIFTY HFT data (NEW - separate from NIFTY!)
        if (bankniftyHftStatus?.engine) {
          const engine = bankniftyHftStatus.engine;
          // STRICT DATA CONTRACT: Use session_cvd_absolute as primary CVD source
          const bnCvd = engine.session_cvd_absolute || engine.cvd || engine.cumulative_delta || bankniftyHftStatus.cvd || 0;
          console.log('📊 [MarketProvider] BANKNIFTY HFT Update:', {
            session_cvd_absolute: engine.session_cvd_absolute,
            engine_cvd: engine.cvd,
            engine_cumulative_delta: engine.cumulative_delta,
            top_level_cvd: bankniftyHftStatus.cvd,
            final_cvd: bnCvd,
            session_volume: engine.session_volume,
            candle_count: engine.candle_count,
          });
          updateSymbolData('BANKNIFTY', {
            sessionStats: {
              cumulative_delta: bnCvd,
              session_volume: engine.session_volume || bankniftyHftStatus.session_volume || 0,
              vwap: engine.vwap || 0,
              tick_count: engine.candle_count || 0,
            },
            hftSignal: {
              signal: engine.signal,
              signal_state: engine.signal_state,
              probability: engine.probability,
              velocity: engine.velocity,
              delta: engine.delta,
            },
          });
        }
        
        if (niftyGodScore) {
          updateSymbolData('NIFTY', { godScore: niftyGodScore });
        }
        
        // Update BANKNIFTY God Score (now calculated separately!)
        if (bankniftyGodScore) {
          console.log('📊 [MarketProvider] BANKNIFTY God Score received:', {
            total: bankniftyGodScore.total_score,
            hft: bankniftyGodScore.hft_score,
            gamma: bankniftyGodScore.gamma_score,
            profile: bankniftyGodScore.profile_score,
          });
          updateSymbolData('BANKNIFTY', { godScore: bankniftyGodScore });
        }
        
        if (niftyGex) {
          updateSymbolData('NIFTY', { 
            gexLevels: niftyGex,
            futuresPrice: niftyGex.futures_price || 0,
            spotPrice: niftyGex.spot_price || 0,
          });
        }
        
        if (bankniftyGex) {
          updateSymbolData('BANKNIFTY', { 
            gexLevels: bankniftyGex,
            futuresPrice: bankniftyGex.futures_price || 0,
            spotPrice: bankniftyGex.spot_price || 0,
          });
        }
        
        // Update NIFTY Market Profile
        if (niftyProfile) {
          updateSymbolData('NIFTY', {
            marketProfile: niftyProfile,
            dpocLineData: niftyProfile.dpoc_history || [],
          });
        }
        
        // Update BANKNIFTY Market Profile
        if (bankniftyProfile) {
          updateSymbolData('BANKNIFTY', {
            marketProfile: bankniftyProfile,
            dpocLineData: bankniftyProfile.dpoc_history || [],
          });
        }
        
        // Fetch candle data for charts
        const candlesRes = await fetch(`${API}/api/candles/1s?limit=100`, { headers }).catch(() => null);
        if (candlesRes?.ok) {
          const candlesData = await candlesRes.json();
          if (candlesData?.candles?.length > 0) {
            // Normalize timestamps and sort by time
            const normalizedCandles = candlesData.candles
              .map(c => ({
                ...c,
                time: c.time > 10000000000 ? Math.floor(c.time / 1000) : c.time,
              }))
              .sort((a, b) => a.time - b.time);
            
            // Filter by price range to separate NIFTY and BANKNIFTY
            const niftyCandles = normalizedCandles.filter(c => c.close >= 15000 && c.close <= 35000);
            const bankniftyCandles = normalizedCandles.filter(c => c.close >= 35000 && c.close <= 80000);
            
            setStore(prev => ({
              ...prev,
              NIFTY: {
                ...prev.NIFTY,
                candles: niftyCandles,
                hftCandles: niftyCandles,
                latestCandle: niftyCandles[niftyCandles.length - 1],
              },
              BANKNIFTY: {
                ...prev.BANKNIFTY,
                candles: bankniftyCandles,
                hftCandles: bankniftyCandles,
                latestCandle: bankniftyCandles[bankniftyCandles.length - 1],
              },
            }));
          }
        }
        
      } catch (err) {
        console.error('[MarketProvider] Polling error:', err);
      }
    }, 2000);
    
  }, [updateSymbolData]);
  
  // Set the ref so connect() can access it
  useEffect(() => {
    startPollingRef.current = startPolling;
  }, [startPolling]);
  
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    isPollingRef.current = false;
  }, []);
  
  // === INITIALIZE ON MOUNT ===
  useEffect(() => {
    console.log('[MarketProvider] Initializing...');
    
    // Check for token and connect
    const token = localStorage.getItem('token');
    if (token) {
      // Fetch initial data from REST APIs
      fetchInitialData();
      
      // Connect WebSocket
      connect();
      
      // Start polling as fallback after a delay (if WebSocket hasn't connected)
      setTimeout(() => {
        if (!isConnectedRef.current) {
          console.log('[MarketProvider] WebSocket not connected after 5s, starting polling fallback');
          startPolling();
        }
      }, 5000);
    } else {
      console.log('[MarketProvider] No token yet, waiting for login...');
    }
    
    // Cleanup on unmount (app close only)
    return () => {
      console.log('[MarketProvider] Cleanup');
      clearTimeout(reconnectTimeoutRef.current);
      clearInterval(pingIntervalRef.current);
      clearTimeout(throttleTimerRef.current); // Clean up batching timer
      stopPolling();
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect, fetchInitialData, startPolling, stopPolling]);
  
  // === CONNECT WHEN TOKEN BECOMES AVAILABLE ===
  // This effect runs when the user logs in
  useEffect(() => {
    const checkAndConnect = () => {
      const token = localStorage.getItem('token');
      if (token && !isConnectedRef.current && wsRef.current?.readyState !== WebSocket.OPEN) {
        console.log('[MarketProvider] Token detected, connecting...');
        fetchInitialData();
        connect();
      }
    };

    // Session 147j+16bx — In FOCUS_MODE (AlphaScout + LODR research
    // dashboard), the HFT WebSocket + REST polling fallback have NO
    // purpose. They were generating 9 parallel fetches every 2 seconds
    // (NIFTY/BANKNIFTY hft-status, god-score, gex-levels, market-
    // profile, feed-status) which saturated the browser's 6-conn
    // concurrent limit and starved the dashboard's actual data calls
    // (alphascout/picks, lodr/orders), making LOGIN + dashboard loads
    // feel slow even though the backend was idle. Early-exit here.
    const focusMode = (process.env.REACT_APP_FOCUS_MODE || 'true').toLowerCase() === 'true';
    if (focusMode) {
      console.log('[MarketProvider] FOCUS_MODE=true — skipping WS + REST polling. Live-feed UI hidden.');
      return; // no interval, no WS, no listeners — clean no-op
    }

    // Check periodically for token (in case login happened)
    const interval = setInterval(checkAndConnect, 1000);
    
    // Also listen for storage events (login from another tab)
    window.addEventListener('storage', checkAndConnect);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', checkAndConnect);
    };
  }, [connect, fetchInitialData]);
  
  // === SESSION 70b: INSTANT SYMBOL SWITCH (sub-50ms) ===
  // Changes activeSymbol ONLY - NO fetches, NO loading states
  // Data for both symbols is already cached in the store from fetchInitialData()
  const switchSymbol = useCallback((newSymbol) => {
    if (newSymbol === activeSymbol) return;
    
    // INSTANT: Just update the state variable - no network calls
    console.log(`⚡ [MarketProvider] INSTANT switch: ${activeSymbol} → ${newSymbol}`);
    setActiveSymbol(newSymbol);
    
    // NOTE: We rely on fetchInitialData() having already loaded both symbols' data
    // If data is missing (edge case), the next WebSocket message will populate it
  }, [activeSymbol]);
  
  // === CONTEXT VALUE ===
  const value = {
    // Global store
    store,
    
    // Active symbol
    activeSymbol,
    setActiveSymbol: switchSymbol,
    
    // Connection status (NEVER flickers)
    connectionStatus,
    
    // === AUDIO ALERTS (Session 39) ===
    audioEnabled,
    toggleAudio,
    
    // SESSION 66 FIX: Include active data directly in context value
    // This ensures components re-render when store updates
    activeData: store[activeSymbol] || createDefaultSymbolData(),
    niftyData: store['NIFTY'] || createDefaultSymbolData(),
    bankniftyData: store['BANKNIFTY'] || createDefaultSymbolData(),
    
    // Helpers to get data (keep for backwards compatibility)
    getActiveData: () => store[activeSymbol] || createDefaultSymbolData(),
    getSymbolData: (symbol) => store[symbol] || createDefaultSymbolData(),
    
    // Update methods (for components that need to modify)
    updateSymbolData,
  };
  
  return (
    <MarketContext.Provider value={value}>
      {children}
    </MarketContext.Provider>
  );
};

// === HOOK ===
export const useMarket = () => {
  const context = useContext(MarketContext);
  if (!context) {
    throw new Error('useMarket must be used within MarketProvider');
  }
  return context;
};

export default MarketContext;
